import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Fragment, type ReactElement } from 'react';

import { SliderProductsTypeEnum } from '@/components/Cms/SliderProducts';
import { DetailPageDocument } from '@/graphql/contentful/api';
import type { OrderAppProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import type {
  ProductVariantStrapBasicDetailsFragment,
  ProductVariantStrapDetailsFragment,
  StrapBuckleSizesQuery,
} from '@/graphql/saleor/api';
import { useSendLandingAlgoliaEvent } from '@/hooks/useSendLandingAlgoliaEvent';
import type { CmsExtraDataRecord } from '@/integrations/contentful';
import {
  type ContentfulData,
  generateSectionKey,
} from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { ATTRIBUTES_SLUG } from '@/lib/const';
import type { Maybe } from '@/lib/types';
import { formatProductSlug } from '@/lib/util';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import type { StrapSectionsOpts } from '@/views/StrapView/cms';
import { renderContentfulStrapSection } from '@/views/StrapView/cms';

import { useRecentlyViewedProducts } from '../WatchView/helper';

export interface StrapViewProps {
  buckleSizesData: StrapBuckleSizesQuery;
  businessData: OrderAppProductVariantsBusinessDetailsQuery;
  cmsData: ContentfulData[];
  cmsExtraDataRecord: CmsExtraDataRecord;
  data: ProductVariantStrapBasicDetailsFragment;
  draftMode: boolean;
  productVariant: Maybe<ProductVariantStrapDetailsFragment>;
}

export const StrapView = ({
  buckleSizesData,
  businessData: businessDataFromSSR,
  cmsData,
  cmsExtraDataRecord,
  data: {
    product: { strapMaterial },
    sku,
  },
  data: basicDetails,
  draftMode,
  productVariant,
}: StrapViewProps) => {
  const liveCmsData = useContentfulLiveUpdates(cmsData, {
    query: DetailPageDocument,
  });
  const materialSlug = strapMaterial?.values[0]?.slug;
  const product = basicDetails.product;
  const recentlyViewedProductReference = useRecentlyViewedProducts(sku);
  useSendLandingAlgoliaEvent(sku);

  const cmsSectionOpts: StrapSectionsOpts = [
    {
      type: 'PlaceholderProductDetails',
      opts: {
        businessData: businessDataFromSSR,
        variantBasicDetails: basicDetails,
        variantDetails: productVariant,
      },
    },
    {
      type: 'Placeholder',
      opts: {
        type: 'key features',
        product,
        buckleSizesData,
      },
    },
    {
      type: 'SliderProductsAutomatic',
      opts: {
        type: SliderProductsTypeEnum.RecentlyViewed,
        filter: {
          slugs: recentlyViewedProductReference.map(formatProductSlug),
        },
        excludeSlug: basicDetails.product.slug,
      },
    },
  ];

  if (materialSlug) {
    cmsSectionOpts.push({
      type: 'SliderProductsAutomatic',
      opts: {
        type: SliderProductsTypeEnum.YouMayAlsoLike,
        excludeSlug: basicDetails.product.slug,
        filter: {
          attributes: [
            {
              slug: ATTRIBUTES_SLUG.wbrMaterialMktg,
              values: [materialSlug],
            },
          ],
        },
      },
    });
  }

  return (
    <>
      {liveCmsData.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {renderContentfulStrapSection(
            data,
            cmsSectionOpts,
            cmsExtraDataRecord,
            draftMode,
          )}
        </Fragment>
      ))}
    </>
  );
};

StrapView.getLayout = (page: ReactElement) => {
  return (
    <DraftModeContext.Provider value={page.props.draftMode}>
      <Default
        alertBannerHiddenOnMobile
        cmsData={page.props.cmsDefaultLayoutData}
      >
        {page}
      </Default>
    </DraftModeContext.Provider>
  );
};
